import { Fragment, useState, useEffect, useRef, useCallback } from 'react';
import { getCurrentOrg, listOrgJobs, killOrgJob, killMyOrgJobs } from '../api/orgs';
import { getUser } from '../api/auth';
import { isAdminRole } from '../constants/roles';
import { useConfirm } from './ConfirmDialog';
import { groupJobsByTile } from './processMonitorGroups';
import AgentMonitorView from './AgentMonitorView';
import StorageUsageView from './StorageUsageView';
import { MonitorIcon } from './icons';
import './ProcessMonitorButton.css';

// stage -> human label. Mirrors AdminPage's map; adds am_study_track.
const STAGE_LABELS = {
  mesh: 'Mesh',
  bc: 'BC face map',
  solver: 'FEM solver',
  spt_solver: 'SPT solver',
  lpbf_toolpath: 'LPBF toolpath',
  am_solver: 'AM solver',
  am_study_track: 'AM study track',
  material_calib: 'Material calibration',
};

function fmtDuration(ms) {
  if (!isFinite(ms) || ms < 0) return '—';
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}

function fmtStarted(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/**
 * Org-wide process monitor. Self-contained — fetches the caller's org + identity
 * itself, so it needs no props threaded through the page.
 * variant: "full" = labeled button; "icon" = icon-only (tight toolbars).
 */
export default function ProcessMonitorButton({ variant = 'full', className = '' }) {
  const confirm = useConfirm();
  const [open, setOpen] = useState(false);
  const [orgId, setOrgId] = useState(null);
  const [canKillAny, setCanKillAny] = useState(false); // admin/owner can stop anyone's job
  const [tier, setTier] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [capacity, setCapacity] = useState({
    seatsLimit: 0,
    jobsRunning: 0,
    jobsQueued: 0,
    cpu: { running: 0, queued: 0, limit: 0 },
    gpu: { running: 0, queued: 0, limit: 0 },
  });
  const [compute, setCompute] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [visible, setVisible] = useState(() => document.visibilityState === 'visible');
  const [expanded, setExpanded] = useState({}); // group key -> expanded; missing = collapsed
  const [activeTab, setActiveTab] = useState('processes'); // 'processes' | 'agents' | 'memory'
  const pollRef = useRef(null);

  const refresh = useCallback(async (id) => {
    if (!id) return;
    try {
      const data = await listOrgJobs(id);
      setJobs(data.jobs || []);
      setCapacity({
        seatsLimit: data.seatsLimit || 0,
        jobsRunning: data.jobsRunning || 0,
        jobsQueued: data.jobsQueued || 0,
        // Resource usage (CPUs/GPUs in use vs. license limits). Fall back to the
        // seat counts if an older backend doesn't send the blocks yet.
        cpu: data.cpu || {
          running: data.jobsRunning || 0,
          queued: data.jobsQueued || 0,
          limit: data.seatsLimit || 0,
        },
        gpu: data.gpu || { running: 0, queued: 0, limit: 0 },
      });
      setCompute(data.compute || null);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  // On open: resolve org + identity, then load jobs.
  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setLoading(true);
    (async () => {
      try {
        const [org, user] = await Promise.all([getCurrentOrg(), getUser()]);
        if (!active) return;
        const oid = org?.orgId || null;
        setOrgId(oid);
        setCanKillAny(isAdminRole(org?.role));
        setTier(org?.tier || null);
        setCurrentUserId(user?.userId || user?.username || null);
        if (oid) await refresh(oid);
      } catch (err) {
        if (active) setError(err.message);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [open, refresh]);

  // Track tab visibility so a backgrounded tab can pause polling.
  useEffect(() => {
    const onVis = () => setVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  // Poll jobs every 3s while open AND visible; refresh immediately on regaining
  // visibility so a returning tab isn't stale (mirrors AdminPage's jobsPoll).
  // Gated on the Processes tab being the one showing: the Agents tab runs its own
  // poll, so polling both endpoints while the reader can only see one is waste.
  useEffect(() => {
    if (!open || !orgId || !visible || activeTab !== 'processes') return undefined;
    refresh(orgId);
    pollRef.current = setInterval(() => refresh(orgId), 3000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [open, orgId, visible, activeTab, refresh]);

  // 1s ticker re-renders live durations — only while visible with jobs to tick
  // (running and queued jobs both show a live duration).
  useEffect(() => {
    if (!open || !visible || activeTab !== 'processes' || jobs.length === 0) return undefined;
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [open, visible, activeTab, jobs.length]);

  // Esc closes.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  async function handleKill(job) {
    const label = STAGE_LABELS[job.stage] || job.stage;
    const mine = job.ownerUserId === currentUserId;
    const who = mine ? '' : ` (owned by ${job.displayName || job.ownerUserId})`;
    if (
      !(await confirm({
        title: 'Stop process?',
        body: `Stop ${label}${who}? This kills the job and frees its slot.`,
        danger: true,
        confirmText: 'Stop',
      }))
    )
      return;
    try {
      await killOrgJob(orgId, job.jobId);
      setJobs((prev) => prev.filter((j) => j.jobId !== job.jobId)); // optimistic; poll reconciles
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleKillAllMine() {
    const n = jobs.filter((j) => j.ownerUserId === currentUserId).length;
    if (!n) return;
    if (
      !(await confirm({
        title: 'Stop all your processes?',
        body: `Stop all ${n} of your processes? Running jobs are killed and queued ones removed.`,
        danger: true,
        confirmText: 'Stop all',
      }))
    )
      return;
    try {
      await killMyOrgJobs(orgId);
      setJobs((prev) => prev.filter((j) => j.ownerUserId !== currentUserId)); // optimistic; poll reconciles
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleKillGroup(group) {
    const label = STAGE_LABELS[group.stage] || group.stage;
    const n = group.jobs.length;
    const mine = group.ownerUserId === currentUserId;
    const who = mine ? '' : ` (owned by ${group.displayName || group.ownerUserId})`;
    const where = `${group.projectName || '—'} · ${group.nodeLabel || group.nodeId || '—'}`;
    if (
      !(await confirm({
        title: 'Stop all in group?',
        body: `Stop all ${n} ${label} jobs in ${where}${who}? Running jobs are killed and queued ones removed.`,
        danger: true,
        confirmText: `Stop all (${n})`,
      }))
    )
      return;
    const ids = new Set(group.jobs.map((j) => j.jobId));
    try {
      const results = await Promise.allSettled(group.jobs.map((j) => killOrgJob(orgId, j.jobId)));
      setJobs((prev) => prev.filter((j) => !ids.has(j.jobId))); // optimistic; poll reconciles
      const failed = results.find((r) => r.status === 'rejected');
      if (failed) setError(failed.reason?.message || 'Some jobs could not be stopped.');
    } catch (err) {
      setError(err.message);
    }
  }

  // Collapse fan-out siblings into per-tile groups (mine-first ordering preserved).
  const groups = groupJobsByTile(jobs, currentUserId);
  const myJobCount = jobs.filter((j) => j.ownerUserId === currentUserId).length;

  const toggleGroup = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  // One job -> one <tr>. `child` indents the row and lightens the source line
  // (already shown on the parent group row). Used for single-job groups (no child)
  // and for a group's expanded track rows (child).
  function renderJobRow(j, { child = false } = {}) {
    const mine = j.ownerUserId === currentUserId;
    // running: elapsed since startedAt; queued: time waiting since enqueuedAt.
    const startRef = j.status === 'running' ? j.startedAt || j.enqueuedAt : j.enqueuedAt;
    const startMs = Date.parse(startRef || '');
    const dur = isNaN(startMs) ? -1 : nowTick - startMs;
    const rt = j.resourceType || 'cpu';
    const canStop = mine || canKillAny;
    return (
      <tr
        key={j.jobId}
        className={`${mine ? 'procmon-row-mine' : ''}${child ? ' procmon-row-child' : ''}`}
      >
        <td>
          <div className="procmon-name">
            {STAGE_LABELS[j.stage] || j.stage}
            {j.jobLabel ? ` ${j.jobLabel}` : ''}
            {j.cpuUnits > 1 ? ` · ${j.cpuUnits} CPU workers` : ''}
          </div>
          {!child && (
            <div
              className="procmon-source"
              title={j.projectId ? `${j.projectId} · ${j.nodeId}` : undefined}
            >
              {(j.projectName || (j.projectId ? `${String(j.projectId).slice(0, 8)}…` : '—')) +
                ' · ' +
                (j.nodeLabel || j.nodeId || '—')}
            </div>
          )}
        </td>
        <td>
          {j.displayName || j.ownerUserId}
          {mine && <span className="procmon-you"> (you)</span>}
        </td>
        <td>{fmtStarted(startRef)}</td>
        <td>{fmtDuration(dur)}</td>
        <td>
          <span className={`procmon-chip procmon-chip-${rt}`}>{rt.toUpperCase()}</span>
        </td>
        <td>
          {j.status === 'running' ? (
            <span className="procmon-status procmon-status-running">running</span>
          ) : (
            <span className="procmon-status procmon-status-queued">queued #{j.queuePosition}</span>
          )}
        </td>
        <td>
          {canStop && (
            <button className="procmon-stop" onClick={() => handleKill(j)}>
              Stop
            </button>
          )}
        </td>
      </tr>
    );
  }

  // A collapsed summary row for a multi-job group + (when expanded) its track rows.
  function renderGroup(g) {
    const open = !!expanded[g.key];
    const rt = g.resourceType || 'cpu';
    const canStop = g.mine || canKillAny;
    // Group timing: earliest start, and the longest-running elapsed for duration.
    let earliest = Infinity;
    let maxDur = -1;
    for (const j of g.jobs) {
      const startRef = j.status === 'running' ? j.startedAt || j.enqueuedAt : j.enqueuedAt;
      const ms = Date.parse(startRef || '');
      if (!isNaN(ms)) {
        earliest = Math.min(earliest, ms);
        maxDur = Math.max(maxDur, nowTick - ms);
      }
    }
    const earliestIso = isFinite(earliest) ? new Date(earliest).toISOString() : null;
    return (
      <Fragment key={g.key}>
        <tr
          className={`procmon-row-group ${g.mine ? 'procmon-row-mine' : ''}`}
          onClick={() => toggleGroup(g.key)}
        >
          <td>
            <div className="procmon-name">
              <button
                type="button"
                className="procmon-group-toggle"
                aria-expanded={open}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleGroup(g.key);
                }}
              >
                {open ? '▾' : '▸'}
              </button>
              {STAGE_LABELS[g.stage] || g.stage}
              <span className="procmon-count">×{g.jobs.length}</span>
            </div>
            <div
              className="procmon-source"
              title={g.projectId ? `${g.projectId} · ${g.nodeId}` : undefined}
            >
              {(g.projectName || (g.projectId ? `${String(g.projectId).slice(0, 8)}…` : '—')) +
                ' · ' +
                (g.nodeLabel || g.nodeId || '—')}
            </div>
          </td>
          <td>
            {g.displayName || g.ownerUserId}
            {g.mine && <span className="procmon-you"> (you)</span>}
          </td>
          <td>{fmtStarted(earliestIso)}</td>
          <td>{fmtDuration(maxDur)}</td>
          <td>
            <span className={`procmon-chip procmon-chip-${rt}`}>{rt.toUpperCase()}</span>
          </td>
          <td>
            <span className="procmon-agg">
              {g.runningCount > 0 && (
                <span className="procmon-status procmon-status-running">
                  {g.runningCount} running
                </span>
              )}
              {g.queuedCount > 0 && (
                <span className="procmon-status procmon-status-queued">{g.queuedCount} queued</span>
              )}
            </span>
          </td>
          <td>
            {canStop && (
              <button
                className="procmon-stop"
                onClick={(e) => {
                  e.stopPropagation();
                  handleKillGroup(g);
                }}
              >
                Stop {g.jobs.length}
              </button>
            )}
          </td>
        </tr>
        {open && g.jobs.map((j) => renderJobRow(j, { child: true }))}
      </Fragment>
    );
  }

  const triggerTitle = 'Organization processes';

  return (
    <>
      <button
        type="button"
        className={`procmon-trigger procmon-trigger-${variant} ${className}`}
        onClick={() => setOpen(true)}
        title={triggerTitle}
        aria-label={triggerTitle}
      >
        {variant === 'icon' ? (
          <MonitorIcon />
        ) : (
          <>
            <span className="dash-nav-icon">
              <MonitorIcon />
            </span>
            <span className="dash-nav-label">Processes</span>
          </>
        )}
      </button>

      {open && (
        <div className="procmon-overlay" onClick={() => setOpen(false)}>
          <div className="procmon-modal" onClick={(e) => e.stopPropagation()}>
            <div className="procmon-header">
              <div className="procmon-tabs">
                <button
                  type="button"
                  className={`procmon-tab${activeTab === 'processes' ? ' active' : ''}`}
                  onClick={() => setActiveTab('processes')}
                >
                  Processes
                </button>
                <button
                  type="button"
                  className={`procmon-tab${activeTab === 'agents' ? ' active' : ''}`}
                  onClick={() => setActiveTab('agents')}
                >
                  Agents
                </button>
                <button
                  type="button"
                  className={`procmon-tab${activeTab === 'memory' ? ' active' : ''}`}
                  onClick={() => setActiveTab('memory')}
                >
                  Memory
                </button>
              </div>
              <button className="procmon-close" onClick={() => setOpen(false)} aria-label="Close">
                ✕
              </button>
            </div>

            {activeTab === 'memory' ? (
              <StorageUsageView onNavigate={() => setOpen(false)} />
            ) : activeTab === 'agents' ? (
              <AgentMonitorView
                orgId={orgId}
                currentUserId={currentUserId}
                canKillAny={canKillAny}
              />
            ) : (
              <>
                {!orgId && !loading && (
                  <p className="procmon-empty">You're not part of an organization yet.</p>
                )}

                {orgId && (
                  <>
                    <div className="procmon-capacity">
                      <div className="procmon-meters">
                        <div className="procmon-meter">
                          <span className="procmon-meter-count">
                            {capacity.cpu.running} / {capacity.cpu.limit || '—'}
                          </span>
                          <span className="procmon-meter-label">
                            CPUs in use
                            {capacity.cpu.queued ? ` · ${capacity.cpu.queued} queued` : ''}
                          </span>
                        </div>
                        <div className="procmon-meter">
                          <span className="procmon-meter-count">
                            {capacity.gpu.running} / {capacity.gpu.limit || '—'}
                          </span>
                          <span className="procmon-meter-label">
                            GPUs in use
                            {capacity.gpu.queued ? ` · ${capacity.gpu.queued} queued` : ''}
                          </span>
                        </div>
                      </div>
                      <div className="procmon-meta">
                        {tier && <span className="procmon-tier">Plan: {tier}</span>}
                        {compute && (
                          <span className="procmon-compute" title="Compute backing the jobs">
                            {compute.type === 'cpu' ? 'CPU' : String(compute.type).toUpperCase()}
                            {' · '}
                            {compute.taskCount} task{compute.taskCount === 1 ? '' : 's'}
                            {' · '}
                            {compute.vcpu} vCPU / {Math.round(compute.memMB / 1024)} GB
                            {' · '}cap {compute.machineCap}
                          </span>
                        )}
                        {compute?.cpuPool?.enabled && compute.cpuPool.desired > 0 && (
                          <span
                            className="procmon-compute"
                            title="CPU worker pool ramp-up. Workers take 1-2 min to start; a slow ramp (running < requested for several minutes) usually means the AWS Fargate vCPU quota is throttling task placement while the previous fleet drains."
                          >
                            workers {compute.cpuPool.running}/{compute.cpuPool.desired} up
                            {compute.cpuPool.pending > 0
                              ? ` · ${compute.cpuPool.pending} starting`
                              : ''}
                          </span>
                        )}
                        {myJobCount > 0 && (
                          <button
                            className="procmon-stop procmon-stop-all"
                            onClick={handleKillAllMine}
                            title="Kill every process you own (running and queued)"
                          >
                            Stop all mine ({myJobCount})
                          </button>
                        )}
                      </div>
                    </div>

                    {error && <div className="procmon-error">{error}</div>}

                    {groups.length === 0 ? (
                      <p className="procmon-empty">
                        {loading ? 'Loading…' : 'No processes running or queued right now.'}
                      </p>
                    ) : (
                      <table className="procmon-table">
                        <thead>
                          <tr>
                            <th>Process</th>
                            <th>Owner</th>
                            <th>Started</th>
                            <th>Duration</th>
                            <th>Resource</th>
                            <th>State</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {groups.map((g) =>
                            g.jobs.length === 1 ? renderJobRow(g.jobs[0]) : renderGroup(g)
                          )}
                        </tbody>
                      </table>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
